// lib
import { useState, useRef, useEffect, useCallback } from 'react'
import PropTypes from 'prop-types'

import ADND from '@aytm/lib/adnd/ADND'
import PageGlobals from '@aytm/app/stats/lib/page/page-globals'

// components
import Popup from '@aytm/lib/rfd-ui/components/basic/popup'
import SidebarPopup from '@aytm/app/stats/page/sidebar/components/SidebarPopup'
import { pressable } from '@aytm/app/stats/components/lib/pressable'

// styles
import style from '@aytm/app/stats/page/sidebar/components/questions/styles/CustomQuestion.module.scss'

const CustomQuestionButton = ({ dataTestID, root, item, features, disabled, addQuestion, onDNDStart, onDNDEnd }) => {
  const [blocked, setBlocked] = useState(false)

  const timer = useRef(null)

  const dndListItemRef = useRef()

  const createCustomQuestion = useCallback(() => Object.assign({}, item.default), [item])

  const onClick = useCallback(() => {
    setBlocked(true)

    const question = createCustomQuestion()
    addQuestion(null, question)

    timer.current && clearTimeout(timer.current)
    timer.current = setTimeout(() => setBlocked(false), 1000)

    return () => timer.current && clearTimeout(timer.current)
  }, [addQuestion, createCustomQuestion])

  const dndListItem =
    disabled || features[item.feature].disabled
      ? null
      : ADND.sources
          .data({
            type: `question-custom-${PageGlobals.WINDOW_UID}`,
            clearDragImage: false
          })
          .onStart(onDNDStart)
          .onEnd(onDNDEnd)
          .fetchData(createCustomQuestion)

  useEffect(() => {
    if (dndListItem) dndListItem.connect(dndListItemRef.current)

    return () => {
      if (dndListItem) dndListItem.disconnect()
    }
  }, [dndListItem])

  return (
    <div className={style.row}>
      {disabled ? (
        <div ref={dndListItemRef} data-testid={dataTestID || null} className={`${style.button} p-disabled`}>
          <span className={style['button-text']}>
            {item.icon ? <i className={`icon-aytmfd-${item.icon}`} /> : null}
            {item.text}
          </span>
        </div>
      ) : features[item.feature].available ? (
        <Popup
          targetClassName="p-col-12"
          width="254px"
          trigger="hover"
          direction="topleft"
          content={() => (
            <div>
              <p>
                {/* PME2 (AYTM-11321): render server-authored `text` verbatim; guard the icon, which
                    the already-on-Enterprise Contact-Us variant omits. */}
                {features[item.feature].onPackage.icon ? (
                  <span className={`stat-upgrade-icon stat-upgrade-icon-${features[item.feature].onPackage.id}`}>
                    <i className={`icon-aytmfd-${features[item.feature].onPackage.icon}`} />
                  </span>
                ) : null}
                <span dangerouslySetInnerHTML={{ __html: features[item.feature].onPackage.text }} />
              </p>
            </div>
          )}
        >
          <a
            data-testid={dataTestID || null}
            className={style.button}
            href={features[item.feature].onPackage.upgradeURL}
            rel="noreferrer"
          >
            <span className={style['button-text']}>
              {item.icon ? <i className={`icon-aytmfd-${item.icon}`} /> : null}
              <span dangerouslySetInnerHTML={{ __html: item.text }}></span>
            </span>
          </a>
        </Popup>
      ) : features[item.feature].disabled ? (
        <Popup
          targetClassName="p-col-12"
          width="254px"
          trigger="hover"
          direction="topleft"
          content={() => (
            <div>
              <p>
                Please login/join the team to unlock all features
                <br />
                of the stat report
              </p>
            </div>
          )}
        >
          <div ref={dndListItemRef} data-testid={dataTestID || null} className={style.button}>
            <span className={style['button-text']}>
              {item.icon ? <i className={`icon-aytmfd-${item.icon}`} /> : null}
              <span dangerouslySetInnerHTML={{ __html: item.text }}></span>
            </span>
          </div>
        </Popup>
      ) : (
        <div
          ref={dndListItemRef}
          data-testid={dataTestID || null}
          className={style.button}
          {...pressable(blocked ? null : onClick)}
        >
          <span className={style['button-text']}>
            {item.icon ? <i className={`icon-aytmfd-${item.icon}`} /> : null}
            {item.text}
          </span>
          <span className={style['button-handle']}>
            <i className="icon-aytmfd-handle" />
          </span>
        </div>
      )}
      {0 < item.help.length && root ? <SidebarPopup root={root} help={item.help} width="300px" /> : null}
    </div>
  )
}

CustomQuestionButton.propTypes = {
  dataTestID: PropTypes.string,
  root: PropTypes.any,
  item: PropTypes.object,
  features: PropTypes.object,
  disabled: PropTypes.bool,
  addQuestion: PropTypes.func,
  onDNDStart: PropTypes.func,
  onDNDEnd: PropTypes.func
}

CustomQuestionButton.displayName = 'CustomQuestionButton'

export default CustomQuestionButton
